import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { api, ApiRequestError } from "../../api";
import { authStore } from "../../auth/store";
import { Turnstile, turnstileSiteKey } from "../../auth/Turnstile";
import { BusyButton, Field } from "../../components/ui";
import { ComingSoon } from "../../features/launch/ComingSoon";
import { AuthFrame } from "./LoginPage";

export function passwordProblems(pw: string): string | null {
  if (pw.length < 12) return "Use at least 12 characters.";
  if (!/[a-z]/.test(pw) || !/[A-Z0-9]/.test(pw)) return "Mix lower-case with upper-case letters or digits.";
  if (/^(.)\1+$/.test(pw)) return "That password is too repetitive.";
  return null;
}

type Plan = "free" | "team" | "business";

function asPlan(plan: string | undefined): Plan {
  return plan === "team" || plan === "business" ? plan : "free";
}

export function SignupPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { invite?: string; plan?: Plan; token?: string; email?: string };
  const selectedPlan = asPlan(search.plan);
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [email, setEmail] = useState(search.email ?? "");
  const [password, setPassword] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileTry, setTurnstileTry] = useState(0);
  const [pending, setPending] = useState<{ email: string; plan: Plan } | null>(null);
  const [linkState, setLinkState] = useState<"loading" | "ready" | "bad">(search.token ? "loading" : "ready");
  // The form shows right away. The server refuses a closed signup anyway, so
  // these only switch the page to its invite-only or coming-soon state once
  // the answer arrives.
  const [signupOpen, setSignupOpen] = useState(true);
  const [productOpen, setProductOpen] = useState(true);
  const pwErr = touched ? passwordProblems(password) : null;

  useEffect(() => {
    if (search.invite) return;
    let cancel = false;
    Promise.all([api.setup.status().catch(() => null), api.launch.availability().catch(() => null)])
      .then(([s, a]) => {
        if (cancel) return;
        setProductOpen(a ? a.open !== false : true);
        setSignupOpen(s ? s.signupOpen !== false : true);
      });
    return () => {
      cancel = true;
    };
  }, [search.invite]);

  useEffect(() => {
    if (!search.token) return;
    let cancel = false;
    api.auth
      .signupPending(search.token)
      .then((p) => {
        if (cancel) return;
        setPending({ email: p.email, plan: asPlan(p.plan) });
        setLinkState("ready");
      })
      .catch(() => {
        if (!cancel) setLinkState("bad");
      });
    return () => {
      cancel = true;
    };
  }, [search.token]);

  const afterSignup = (r: Awaited<ReturnType<typeof api.auth.signup>>) => {
    authStore.applyAuthResult(r);
    if (r.next === "verify_email") {
      void navigate({ to: "/verify-email" });
    } else if (r.planIntent && r.orgs[0]) {
      void navigate({
        to: "/orgs/$org/billing",
        params: { org: r.orgs[0].id },
        search: { plan: r.planIntent },
      });
    } else {
      void navigate({ to: "/" });
    }
  };

  const sendLink = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.auth.signupStart(email, selectedPlan, turnstileToken);
      setSentTo(email);
    } catch (err) {
      setError((err as ApiRequestError).message);
      setTurnstileToken("");
      setTurnstileTry((n) => n + 1);
    } finally {
      setBusy(false);
    }
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (passwordProblems(password)) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.auth.signup({
        email: pending?.email ?? email,
        password,
        name: name || undefined,
        orgName: search.invite ? undefined : orgName || undefined,
        plan: pending?.plan ?? selectedPlan,
        inviteToken: search.invite,
        signupToken: search.token,
      });
      afterSignup(r);
    } catch (err) {
      const ae = err as ApiRequestError;
      setError(ae.status === 409 ? "An account with this email already exists." : ae.message);
    } finally {
      setBusy(false);
    }
  };

  if (!productOpen) {
    return <ComingSoon />;
  }
  if (!signupOpen && !search.invite && !search.token) {
    return (
      <AuthFrame title="Invite only" lead="Ask an administrator for an invitation.">
        <div className="auth-links">
          <span className="muted">Already have an account?</span>
          <Link to="/login">Sign in</Link>
        </div>
      </AuthFrame>
    );
  }

  if (search.token && linkState === "loading") {
    return <AuthFrame title="Create your account" lead="Checking your link." />;
  }

  if (sentTo && !search.token) {
    return (
      <AuthFrame title="Check your email" lead={`We sent a link to ${sentTo}. Open it to finish creating your account.`}>
        <div className="auth-links">
          <button type="button" className="btn ghost sm" onClick={() => setSentTo(null)}>
            Use a different email
          </button>
          <Link to="/login">Sign in</Link>
        </div>
      </AuthFrame>
    );
  }

  if (search.token && pending && linkState === "ready") {
    return (
      <AuthFrame title="Create your account" lead="Your email is confirmed. Choose a password to finish.">
        <form onSubmit={create} noValidate autoComplete="off">
          <Field label="Work email">{(p) => <input {...p} className="input" type="email" value={pending.email} readOnly />}</Field>
          <Field label="Name">{(p) => <input {...p} className="input" value={name} onChange={(e) => setName(e.target.value)} />}</Field>
          <Field label="Organization name">
            {(p) => <input {...p} className="input" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Acme, Inc." />}
          </Field>
          <Field label="Password" hint="At least 12 characters. We check it against known breached passwords." error={pwErr}>
            {(p) => <input {...p} className="input" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} onBlur={() => setTouched(true)} required />}
          </Field>
          {error && (
            <div className="error-box" role="alert">
              {error}
            </div>
          )}
          <BusyButton className="btn primary block" type="submit" busy={busy} busyLabel="Creating…" disabled={!password}>
            Create account
          </BusyButton>
        </form>
      </AuthFrame>
    );
  }

  if (search.invite) {
    return (
      <AuthFrame title="Create your account" lead="Free tier: 1,000 stored sessions a month, no card required.">
        <form onSubmit={create} noValidate autoComplete="off">
          <Field label="Name">{(p) => <input {...p} className="input" value={name} onChange={(e) => setName(e.target.value)} />}</Field>
          <Field label="Work email">{(p) => <input {...p} className="input" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} required />}</Field>
          <Field label="Password" hint="At least 12 characters. We check it against known breached passwords." error={pwErr}>
            {(p) => <input {...p} className="input" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} onBlur={() => setTouched(true)} required />}
          </Field>
          {error && (
            <div className="error-box" role="alert">
              {error}
            </div>
          )}
          <BusyButton className="btn primary block" type="submit" busy={busy} busyLabel="Creating…" disabled={!email || !password}>
            Create account
          </BusyButton>
        </form>
        <div className="auth-links">
          <span className="muted">Already have an account?</span>
          <Link to="/login">Sign in</Link>
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title="Create your account" lead="Free tier: 1,000 stored sessions a month, no card required.">
      <form onSubmit={sendLink} noValidate autoComplete="off">
        <Field label="Work email" error={linkState === "bad" ? "That link has expired. Enter your email to get a new one." : undefined}>
          {(p) => <input {...p} className="input" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} required />}
        </Field>
        <Turnstile key={turnstileTry} onToken={setTurnstileToken} />
        {error && (
          <div className="error-box" role="alert">
            {error}
          </div>
        )}
        <BusyButton className="btn primary block" type="submit" busy={busy} busyLabel="Sending…" disabled={!email.includes("@") || (!!turnstileSiteKey() && !turnstileToken)}>
          Email me a link
        </BusyButton>
      </form>
      <div className="auth-links">
        <span className="muted">Already have an account?</span>
        <Link to="/login">Sign in</Link>
      </div>
    </AuthFrame>
  );
}
